import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { idiomaDeLaRuta } from '@/lib/rutasPublicas';
import { idiomaEfectivoActual } from '@/lib/idiomaSesion';
import { useAuth } from '@/lib/AuthContext';

/**
 * Mantiene el idioma pegado a la URL mientras se navega.
 *
 * El detector de i18next solo corre UNA vez, al arrancar. En un SPA eso no
 * basta: si desde `/hoteles` pulsas el enlace a `/de/hotels`, react-router
 * cambia la ruta sin recargar y sin ese detector la página se quedaría en
 * español con una URL alemana — que es justo la incoherencia que este trabajo
 * viene a quitar, y encima la peor versión: la que solo ve quien navega, no
 * quien entra directo.
 *
 * **Y al SALIR de una ruta que impone idioma, se lo devuelve al usuario.**
 * Antes, en las rutas que no imponen (`/register`, `/login`, el panel) no se
 * tocaba nada, así que el español de la home se arrastraba: un portugués
 * entraba por `/`, pulsaba «Registrarse» y se daba de alta como cliente
 * español. Ahora ahí vale `idiomaEfectivoActual` — su elección de esta sesión,
 * la IP de Portugal, lo que eligió otro día, su navegador y, por último, su
 * cuenta (el orden y el porqué, en `lib/idiomaSesion.js`).
 *
 * Va aquí y no dentro de `LanguageProvider` porque necesita `useLocation()`, y
 * el provider envuelve al `<Router>` desde fuera. Es el mismo motivo por el que
 * `PosthogRecordingGate` vive suelto dentro del Router, con su nota al lado en
 * `App.jsx`.
 *
 * **No pasa por `setLanguage` a propósito.** Esa función persiste la elección
 * en `localStorage`, y lo de aquí no es una elección: es el idioma que impone
 * la dirección que se está visitando. Guardarlo le sobrescribiría la
 * preferencia a un alemán por el mero hecho de abrir un enlace en español.
 *
 * Las dependencias son la ruta y el idioma de la cuenta, NUNCA el idioma
 * actual: si lo fueran, este efecto desharía en el acto cada cambio del
 * selector. El selector no lo necesita: apunta su elección en la sesión, y es
 * lo primero que mira `idiomaEfectivoActual` en la siguiente navegación.
 */
export default function IdiomaSegunRuta() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const cuenta = user?.preferredLanguage;

  useEffect(() => {
    const objetivo = idiomaDeLaRuta(pathname) ?? idiomaEfectivoActual({ cuenta });
    if (i18n.resolvedLanguage === objetivo) return;
    i18n.changeLanguage(objetivo);
  }, [pathname, cuenta, i18n]);

  return null;
}
